import { useEffect, useState, useRef } from "react";
import api, { mediaUrl, formatError, uploadFileChunked } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Film, ImageIcon, Loader2, Plus, Sparkles, Maximize, Video, X } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const CATS = ["Produce", "Meat", "Grocery", "Bakery", "Dairy", "Other"];

export default function Media() {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [sorting, setSorting] = useState(false);
  const [refitting, setRefitting] = useState(false);
  const [filter, setFilter] = useState("All");
  const [videoJobs, setVideoJobs] = useState([]); // [{id, name, pct, error}]
  const fileRef = useRef();
  const videoRef = useRef();

  const load = () => api.get("/media").then((r) => {
    if (Array.isArray(r.data)) setMedia((prev) => (r.data.length === 0 && prev.length > 0 ? prev : r.data));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    let ok = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) {
        toast.error(`${file.name}: ${err.response?.data?.detail || "upload failed"}`);
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    if (ok) toast.success(`${ok} file(s) added & auto-sorted`);
    setUploading(false);
    setProgress({ done: 0, total: 0 });
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.original_filename}"?`)) return;
    try { await api.delete(`/media/${m.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const setCategory = async (m, category) => {
    setMedia((prev) => prev.map((x) => (x.id === m.id ? { ...x, category } : x)));
    try { await api.post(`/media/${m.id}/category`, { category }); }
    catch { toast.error("Could not move"); load(); }
  };

  const autoSort = async () => {
    setSorting(true);
    try {
      const r = await api.post("/media/categorize-all");
      toast.success(`AI sorted ${r.data.updated} item(s)`);
      load();
    } catch { toast.error("Auto-sort failed"); }
    setSorting(false);
  };

  const refit = async () => {
    setRefitting(true);
    try {
      const r = await api.post("/media/refit-all");
      toast.success(r.data.updated ? `Resized ${r.data.updated} image(s) to fill the TV` : "All images already fit");
      load();
    } catch { toast.error("Resize failed"); }
    setRefitting(false);
  };

  const pick = () => fileRef.current?.click();
  const pickVideo = () => videoRef.current?.click();

  const onVideoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) { /* noop */ }
    if (videoRef.current) videoRef.current.value = "";
    for (const file of files) {
      const tooBig = file.size > 500 * 1024 * 1024;
      const jobId = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      if (tooBig) { toast.error(`${file.name}: video is too large (max 500MB)`); continue; }
      setVideoJobs((j) => [...j, { id: jobId, name: file.name, pct: 0, error: null }]);
      try {
        await uploadFileChunked(file, (pct) =>
          setVideoJobs((j) => j.map((x) => (x.id === jobId ? { ...x, pct } : x))));
        toast.success(`${file.name} uploaded`);
        setVideoJobs((j) => j.filter((x) => x.id !== jobId));
        load();
      } catch (err) {
        const msg = formatError(err.response?.data?.detail) || "upload failed";
        toast.error(`${file.name}: ${msg}`);
        setVideoJobs((j) => j.map((x) => (x.id === jobId ? { ...x, error: msg } : x)));
      }
    }
  };

  const dismissJob = (id) => setVideoJobs((j) => j.filter((x) => x.id !== id));

  const counts = CATS.reduce((a, c) => ({ ...a, [c]: media.filter((m) => (m.category || "Other") === c).length }), {});
  const visible = filter === "All" ? media : media.filter((m) => (m.category || "Other") === filter);

  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8">
      <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={onUpload} data-testid="media-file-input" />
      <input ref={videoRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/*" multiple hidden onChange={onVideoUpload} data-testid="video-file-input" />

      <div className="mb-4 flex items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-bold tracking-tight">Media Library</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Sorted into folders automatically by AI</p>
        </div>
        <div className="flex-shrink-0 flex flex-col gap-1.5">
          <button onClick={autoSort} disabled={sorting || !media.length} data-testid="auto-sort-btn"
            className="flex items-center gap-1.5 rounded-md bg-secondary px-3 py-2 text-xs font-medium active:bg-secondary/70 disabled:opacity-50">
            {sorting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-primary" />}
            {sorting ? "Sorting…" : "AI Sort"}
          </button>
          <button onClick={refit} disabled={refitting || !media.length} data-testid="refit-btn"
            className="flex items-center gap-1.5 rounded-md bg-secondary px-3 py-2 text-xs font-medium active:bg-secondary/70 disabled:opacity-50">
            {refitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Maximize className="w-4 h-4 text-primary" />}
            {refitting ? "Resizing…" : "Fix Sizes"}
          </button>
        </div>
      </div>

      {/* Add zone */}
      <button onClick={pick} disabled={uploading} data-testid="upload-button"
        className="w-full flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 active:bg-primary/10 transition-colors py-7 mb-3 disabled:opacity-60">
        {uploading ? (
          <><Loader2 className="w-8 h-8 text-primary animate-spin" /><span className="text-sm font-medium text-primary">Uploading {progress.done}/{progress.total}…</span></>
        ) : (
          <>
            <div className="w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center"><Plus className="w-6 h-6" /></div>
            <span className="text-base font-semibold">Add Photos / Videos</span>
            <span className="text-xs text-muted-foreground">Tap to pick several — auto-sorted into folders</span>
          </>
        )}
      </button>

      {/* Dedicated finished-video uploader (chunked, reliable for large 1080p files) */}
      <button onClick={pickVideo} data-testid="upload-video-button"
        className="w-full flex items-center justify-center gap-2.5 rounded-xl border border-border bg-card active:bg-secondary/60 transition-colors py-3.5 mb-3">
        <Video className="w-5 h-5 text-primary" />
        <span className="text-sm font-semibold">Upload finished video</span>
        <span className="text-xs text-muted-foreground hidden sm:inline">1080p · 1–2 min · up to 500MB</span>
      </button>

      {/* Per-video progress bars */}
      {videoJobs.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="video-upload-progress">
          {videoJobs.map((j) => (
            <div key={j.id} className="rounded-lg border border-border bg-card p-3" data-testid={`video-job-${j.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="text-xs font-medium truncate min-w-0 flex items-center gap-1.5">
                  <Film className="w-3.5 h-3.5 text-primary flex-shrink-0" /> {j.name}
                </span>
                <span className="text-xs flex-shrink-0 tabular-nums text-muted-foreground">
                  {j.error ? <span className="text-destructive">Failed</span> : j.pct >= 100 ? "Finishing…" : `${j.pct}%`}
                </span>
                {j.error && (
                  <button onClick={() => dismissJob(j.id)} className="text-muted-foreground active:text-destructive" data-testid={`dismiss-job-${j.id}`}>
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-300 ${j.error ? "bg-destructive" : "bg-primary"}`}
                  style={{ width: `${j.error ? 100 : j.pct}%` }} />
              </div>
              {j.error && <p className="text-[11px] text-destructive mt-1.5">{j.error}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Filter chips */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-1 px-1">
        {["All", ...CATS].map((c) => {
          const n = c === "All" ? media.length : counts[c];
          const active = filter === c;
          return (
            <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c}`}
              className={`flex-shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground active:bg-secondary/70"}`}>
              {c} {n > 0 && <span className={active ? "opacity-80" : "text-muted-foreground"}>· {n}</span>}
            </button>
          );
        })}
      </div>

      {media.length === 0 ? (
        <div className="border border-dashed border-border rounded-md py-16 text-center">
          <ImageIcon className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">Your library is empty.</p>
          <p className="text-sm text-muted-foreground/70 mt-1">Tap “Add Photos / Videos” to start.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
          {visible.map((m) => (
            <div key={m.id} data-testid={`media-item-${m.id}`} className="bg-card border border-border rounded-lg overflow-hidden fade-up">
              <div className="relative aspect-video bg-black flex items-center justify-center">
                {m.type === "video"
                  ? <video src={mediaUrl(m.id)} className="w-full h-full object-cover" muted preload="metadata" playsInline />
                  : <img src={mediaUrl(m.id)} alt={m.original_filename} className="w-full h-full object-cover" />}
                <span className="absolute top-1.5 left-1.5 bg-black/70 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider flex items-center gap-1">
                  {m.type === "video" ? <Film className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />} {m.type}
                </span>
              </div>
              <div className="p-2.5 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs md:text-sm font-medium truncate min-w-0">{m.original_filename}</p>
                  <button data-testid={`media-delete-${m.id}`} onClick={() => remove(m)} aria-label="Delete"
                    className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-md bg-destructive/10 text-destructive active:bg-destructive/20">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <Select value={m.category || "Other"} onValueChange={(v) => setCategory(m, v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`media-category-${m.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
